import { useEffect, useState } from 'react';
import { formatNetPrice, formatTotal } from '@pr-app/shared';
import {
  Body1,
  Body1Strong,
  Button,
  Caption1,
  Image,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { fetchAttachmentBlob, type Attachment } from '../api/attachments';
import { AttachmentPreviewDialog } from '../components/AttachmentPreviewDialog';
import { DataCard, DataCardList, DataCardTotal } from '../components/DataCardList';
import { ROW_HEIGHT } from '../components/DataTable';
import { Section } from '../components/Section';
import { useIsNarrowViewport } from '../hooks/useIsNarrowViewport';
import { useResponsiveStyles } from '../hooks/useResponsiveStyles';
import { useRestoreFocusOnClose } from '../hooks/useRestoreFocusOnClose';
import { truncateFileNameMiddle } from '../lib/fileNameDisplay';
import { LAYOUT, RADIUS, SPACE, SPACE_X } from '../styles/scale';
import { useSurfaceStyles } from '../styles/surfaces';

/**
 * SOW §4.5, wireframe 04 (R5) — the full request in one view before it leaves
 * the requester's hands: everything the reviewer will see, in the same order
 * (V2), with the two vendor-facing line fields always visible here (they're
 * behind an expander while editing, but at review completeness matters more
 * than density).
 *
 * ── Design system pass (#202) ──
 * This screen is almost entirely label/value pairs, and the app had three
 * separate hand-rolled versions of that pattern with no agreed alignment, gap
 * or typography. One shape now, defined by `pair`/`pairLabel`/`pairValue`
 * below: a `Caption1` label in `colorNeutralForeground2` 4px above a
 * `Body1Strong` value, with 16px between one pair and the next — so the eye
 * groups a label with its own value rather than with the pair beside it.
 *
 * The three blocks are `Section`s (a real `<h3>` on a card), and the readiness
 * statement is a Fluent `MessageBar`. It used to be a hand-built green or red
 * panel with no icon and no title, which meant the two states were told apart
 * by HUE ALONE — identical under `teamsHighContrastTheme`, where the palette
 * collapses. `MessageBar` brings the intent icon, the announcement and the
 * title, and is the only place the design system permits `colorPalette*` at
 * all.
 */

export interface ReviewDetailField {
  label: string;
  value: string;
}

export interface ReviewLine {
  id: string;
  itemDescription: string;
  categoryLabel: string;
  quantity: string;
  unitOfMeasure: string;
  /** Raw, unformatted decimal string (#148) — this component formats it with
   * `formatNetPrice`/`currencyCode` itself, so the grouping/precision rule lives
   * in exactly one place regardless of which screen builds a `ReviewLine`. */
  netPrice: string;
  /** Raw, unformatted decimal string — see `netPrice`; formatted here with `formatTotal`. */
  lineTotal: string;
  deliveryDate: string;
  startDate: string;
  endDate: string;
  /** `CODE — description`, already resolved by the caller; empty when none was chosen. */
  salesTaxLabel: string;
}

const useStyles = makeStyles({
  /** The review block's own rhythm: readiness bar and the three section cards
   *  are siblings at the page's default 16px step, owned here and nowhere else. */
  stack: { display: 'flex', flexDirection: 'column', gap: SPACE.l, minWidth: '0' },

  /**
   * R5/R2b — the bar states the problem, the list underneath is what you click.
   * The buttons stay OUT of `MessageBarBody`: Fluent expects actions in
   * `MessageBarActions` and misbehaves with a button in the body (CLAUDE.md log
   * entry 9, and the same reason `ErrorBoundary` hand-builds its panel). 8px,
   * because the bar and its list are one block.
   */
  readiness: { display: 'flex', flexDirection: 'column', gap: SPACE.s },
  issueList: {
    margin: SPACE.none,
    paddingInlineStart: SPACE_X.xxl,
  },

  /**
   * Wide space is filled by ADDING A COLUMN, never by widening one
   * (DESIGN-SYSTEM §4). `auto-fit` lets a wide Teams canvas lay the pairs out
   * six across instead of running one long column down the left — and the
   * 280px cap on each track is what stops a single value being stretched
   * across an 1800px monitor, which is unreadable however much space there is.
   */
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 280px))',
    columnGap: SPACE_X.l,
    rowGap: SPACE.l,
  },
  /** 4px inside a pair against 16px between pairs — the whole point of the grid. */
  pair: { display: 'flex', flexDirection: 'column', gap: SPACE.xs, minWidth: '0' },
  pairLabel: { color: tokens.colorNeutralForeground2 },
  /** A long D365 value wraps rather than widening the tab; Teams forbids sideways scroll. */
  pairValue: { overflowWrap: 'anywhere' },

  hint: { color: tokens.colorNeutralForeground2 },

  /**
   * Center alignment, on every line-items header AND its matching cell —
   * same treatment #211/PR #212 gave the Dashboard/Reviewer Queue/Admin
   * `DataTable` (via its `alignCenter`, the default there) and #213/PR #214
   * gave the New Request line-items editor table (#215). This table predates
   * `DataTable` and is raw markup, so the equivalent is applied locally.
   * `TableHeaderCell` puts its children inside a flex
   * `.fui-TableHeaderCell__button` div, so `text-align` alone moves the `<td>`
   * and leaves the `<th>` looking un-aligned beside it — the descendant rule
   * is what actually keeps the pair together.
   */
  alignCenter: {
    textAlign: 'center',
    '& .fui-TableHeaderCell__button': { justifyContent: 'center' },
  },
  /** Tabular figures for the money/quantity columns — kept even though #215
   *  centers them, so a column of numbers still compares by eye. */
  numericValue: { fontVariantNumeric: 'tabular-nums' },

  /**
   * #217 — the card/scroll/row-density wrapper `DataTable` gives Reviewer
   * Queue's line-items table via `useSurfaceStyles().tableCard`, reproduced
   * locally for the same reason every other class in this block is: this
   * table predates `DataTable`, carries a per-line vendor sub-row and a
   * `tfoot` grand-total row `DataTable` has no equivalent for, and is raw
   * markup. `tableCard` is the bordered/rounded/shadowed card; `scroll` is
   * the dedicated `overflowX: auto` element that sits INSIDE it — see
   * `DataTable.tsx`'s own docblock (CLAUDE.md log entry 57) for why the card
   * itself must stay `overflow: hidden` and the scroll element is what
   * absorbs any excess width instead.
   */
  scroll: {
    overflowX: 'auto',
    overflowY: 'hidden',
  },
  /** `auto`, overriding Fluent's `fixed` — same override `DataTable`'s own
   *  `table` class applies, so a wrapped cell can grow the row instead of
   *  the column squashing to fit. */
  table: {
    tableLayout: 'auto',
    width: '100%',
  },

  /**
   * #215 — the shaded header band `DataTable`'s own `headerCell` already
   * gives Reviewer Queue's line-items table (and every other `DataTable`),
   * reproduced locally for the same reason `alignCenter` above is: this table
   * predates `DataTable` and is raw markup. Keeps Review & submit's table
   * looking like the same system as the reviewer's own read-only view of the
   * identical data. Padding/`whiteSpace` (#217) match `DataTable`'s
   * `headerCell` exactly, rather than relying on Fluent's own default.
   */
  headerRow: {
    borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
  },
  headerCell: {
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground2,
    height: ROW_HEIGHT,
    padding: `${SPACE.none} ${SPACE_X.m}`,
    whiteSpace: 'nowrap',
  },

  /**
   * #217 — the body cell's row density, matched to `DataTable`'s own `cell`
   * class exactly: `height: 'auto'` overrides Fluent's fixed 44px
   * `size="medium"` cell height so a wrapped line can grow the row, with
   * `minHeight: ROW_HEIGHT` keeping that 44px as a floor for a short one, and
   * real top/bottom padding so a row reads with breathing room instead of
   * pinned flat against its neighbours — the gap #216 never closed.
   */
  cell: {
    height: 'auto',
    minHeight: ROW_HEIGHT,
    paddingTop: SPACE.m,
    paddingBottom: SPACE.m,
    paddingLeft: SPACE_X.m,
    paddingRight: SPACE_X.m,
    maxWidth: LAYOUT.cellWrap,
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },

  /**
   * The grand-total rule. `strokeWidthThick` rather than a literal 2px, and the
   * weight comes from the `Body1Strong` inside the cell — `fontWeight:` is a
   * banned declaration, because `Body1` + semibold IS `Body1Strong`. Only the
   * border/alignment/numeric parts live here (#217) — the `tfoot` row uses raw
   * `<td>`s, which get none of `TableCell`'s built-in padding, so its markup
   * also applies `styles.cell` alongside this one rather than duplicating
   * `cell`'s padding/`minHeight` values a second time; without `cell` there the
   * footer row read visibly flatter than the body rows above it.
   */
  footerCell: {
    borderTop: `${tokens.strokeWidthThick} solid ${tokens.colorNeutralStroke1}`,
    textAlign: 'center',
    fontVariantNumeric: 'tabular-nums',
  },

  /**
   * #254 — attachments used to be static Fluent `Tag`s with no interaction at
   * all. This screen now reuses `ReviewerQueue.tsx`'s already-working,
   * already-tested thumbnail-grid/lightbox/document-row treatment verbatim
   * (styles included) rather than inventing a fourth visual language for the
   * same concept — see that file's own comments for the reasoning behind each
   * of these rules, in particular why the thumbnail button sets `color`
   * explicitly (CLAUDE.md log entry 54: a native `<button>` does not inherit
   * it from `FluentProvider` the way a `<div>` does).
   */
  thumbnailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap: SPACE_X.m,
  },
  thumbnailButton: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.xs,
    padding: SPACE.none,
    border: 'none',
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground1,
    cursor: 'pointer',
    textAlign: 'start',
  },
  thumbnailImage: {
    width: '100%',
    aspectRatio: '1',
    objectFit: 'cover',
    borderRadius: RADIUS.control,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  thumbnailCaption: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: tokens.colorNeutralForeground2,
  },
  lightboxImage: {
    maxWidth: '100%',
    maxHeight: '70vh',
    display: 'block',
    marginInlineStart: 'auto',
    marginInlineEnd: 'auto',
  },
  documentRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: SPACE_X.m,
  },
  attachmentsBlock: { display: 'flex', flexDirection: 'column', gap: SPACE.m },
});

function isImage(contentType: string): boolean {
  return contentType.startsWith('image/');
}

/**
 * Fetches the object URL once per attachment through the authenticated
 * download route — UNLESS `localFile` is given, in which case it is used
 * directly with no network round trip.
 *
 * #260 — this is the actual root-cause fix, not an optimisation: before,
 * this thumbnail always re-fetched over the network even for an image
 * uploaded seconds ago in the same session, and the `.catch(() => undefined)`
 * left it permanently blank on any transient failure (slow SSO token
 * refresh, network blip, tab losing focus mid-request). `RequestForm.tsx`
 * now keeps the freshly-uploaded `File` alongside its `Attachment` and passes
 * it through as `localFiles`, so this never has to guess.
 */
function useAttachmentImageUrl(attachment: Attachment, localFile?: File): string | undefined {
  const [url, setUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!isImage(attachment.contentType)) return undefined;
    let objectUrl: string | undefined;
    let cancelled = false;
    if (localFile) {
      objectUrl = URL.createObjectURL(localFile);
      setUrl(objectUrl);
    } else {
      setUrl(undefined);
      void fetchAttachmentBlob(attachment.id)
        .then((blob) => {
          if (cancelled) return undefined;
          objectUrl = URL.createObjectURL(blob);
          setUrl(objectUrl);
          return undefined;
        })
        .catch(() => undefined);
    }
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.id, attachment.contentType, localFile]);
  return url;
}

function ReviewSummaryThumbnail({
  attachment,
  localFile,
  onOpen,
}: {
  attachment: Attachment;
  localFile?: File;
  onOpen: () => void;
}) {
  const styles = useStyles();
  const url = useAttachmentImageUrl(attachment, localFile);
  return (
    <button
      type="button"
      className={styles.thumbnailButton}
      onClick={onOpen}
      aria-label={`Open ${attachment.fileName}`}
    >
      {url ? (
        <Image src={url} alt="" className={styles.thumbnailImage} />
      ) : (
        <div className={styles.thumbnailImage}>
          <Spinner size="tiny" />
        </div>
      )}
      {/* X2 note 1: a filename truncates in the MIDDLE, so the extension —
          the part that says what the file is — stays visible. */}
      <Caption1 className={styles.thumbnailCaption} title={attachment.fileName}>
        {truncateFileNameMiddle(attachment.fileName)}
      </Caption1>
    </button>
  );
}

/** One R2b-style validation issue — the message Zod produced, and where clicking it should
 * land. Optional: a caller with no way to link issues to fields still gets the old, plain
 * sentence rather than an empty list. */
export interface ReviewSummaryIssue {
  message: string;
  onClick: () => void;
}

export interface ReviewSummaryProps {
  isValid: boolean;
  details: ReviewDetailField[];
  lines: ReviewLine[];
  /** Raw, unformatted decimal string (#148) — formatted here with `formatTotal`/`currencyCode`,
   * same reasoning as `ReviewLine.netPrice`/`lineTotal` above. */
  grandTotal: string;
  currencyCode: string;
  attachments: Attachment[];
  /**
   * #260 — freshly-uploaded files still in memory this session, keyed by
   * attachment id. Passed straight through to the thumbnail (see
   * `useAttachmentImageUrl`'s docblock) and to `AttachmentPreviewDialog`, so
   * neither has to re-fetch over the network what the wizard already has.
   */
  localFiles?: Map<string, File>;
  /** R5/R2b (#160) — when provided and `isValid` is false, drives a linked error summary
   * instead of the generic "not ready to submit" sentence. */
  issues?: ReviewSummaryIssue[];
  onEditDetails?: () => void;
  onEditLines?: () => void;
  onEditAttachments?: () => void;
  /**
   * Sheet R9 — once a request has left DRAFT/REJECTED it can only be looked
   * at, never edited: no Edit buttons, and no "ready to submit" readiness
   * banner (submission has already happened, so it would be meaningless).
   */
  readOnly?: boolean;
}

/**
 * The Edit affordance for one block, or nothing at all. Returned as `undefined`
 * rather than `false` on purpose: `Section` tests its `action` prop for
 * `undefined`, so a falsy node would still cost an empty action cell on the
 * heading row.
 */
function editAction(readOnly: boolean, onEdit: (() => void) | undefined) {
  if (readOnly || onEdit === undefined) return undefined;
  return (
    <Button appearance="transparent" size="small" onClick={onEdit}>
      Edit
    </Button>
  );
}

export function ReviewSummary({
  isValid,
  details,
  lines,
  grandTotal,
  currencyCode,
  attachments,
  localFiles,
  issues,
  onEditDetails,
  onEditLines,
  onEditAttachments,
  readOnly = false,
}: ReviewSummaryProps) {
  const styles = useStyles();
  const surfaces = useSurfaceStyles();
  const responsive = useResponsiveStyles();
  const isNarrow = useIsNarrowViewport();
  // #260 — one shared preview dialog for every attachment type; the index is
  // into `attachments` (the FULL list), so Previous/Next moves across every type.
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  // K1 "Dialog closes": back to the thumbnail/row that opened it.
  useRestoreFocusOnClose(previewIndex !== null);

  const images = attachments.filter((attachment) => isImage(attachment.contentType));
  const documents = attachments.filter((attachment) => !isImage(attachment.contentType));

  return (
    // `touchTargets` sits outside the `Section` so it also reaches the Edit
    // buttons on each heading row, which are the Section's own children.
    <div className={responsive.touchTargets}>
      {/*
        `surface={false}`: this block's three children are cards, and an 8px
        card inside an 8px card reads wrong. The heading is still a real `<h2>`,
        with the three blocks below it as `<h3>`s — before this the whole screen
        was `Subtitle2` spans and had no outline at all.
      */}
      <Section level={2} surface={false} title={readOnly ? 'Request details' : 'Review & submit'}>
        <div className={styles.stack}>
          {!readOnly &&
            (isValid ? (
              <MessageBar intent="success">
                <MessageBarBody>
                  <MessageBarTitle>Ready to submit</MessageBarTitle>
                  Everything required is filled in. Submitting sends this to the review group — you
                  will not be able to edit it while it is under review.
                </MessageBarBody>
              </MessageBar>
            ) : issues && issues.length > 0 ? (
              <div className={styles.readiness}>
                <MessageBar intent="error">
                  <MessageBarBody>
                    <MessageBarTitle>
                      {issues.length} {issues.length === 1 ? 'thing needs' : 'things need'} fixing
                      before this can be submitted.
                    </MessageBarTitle>
                  </MessageBarBody>
                </MessageBar>
                <ul className={styles.issueList}>
                  {issues.map((issue, index) => (
                    <li key={`${String(index)}:${issue.message}`}>
                      <Button appearance="transparent" onClick={issue.onClick}>
                        {issue.message}
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <MessageBar intent="error">
                <MessageBarBody>
                  <MessageBarTitle>Not ready to submit</MessageBarTitle>
                  This request is not ready to submit yet — Submit for review will list what is
                  missing.
                </MessageBarBody>
              </MessageBar>
            ))}

          <Section level={3} title="Details" action={editAction(readOnly, onEditDetails)}>
            <div className={mergeClasses(styles.detailsGrid, responsive.singleColumn)}>
              {details.map((field) => (
                <div key={field.label} className={styles.pair}>
                  <Caption1 block className={styles.pairLabel}>
                    {field.label}
                  </Caption1>
                  <Body1Strong block className={styles.pairValue}>
                    {field.value || '—'}
                  </Body1Strong>
                </div>
              ))}
            </div>
          </Section>

          <Section
            level={3}
            title="Line items"
            count={lines.length}
            action={editAction(readOnly, onEditLines)}
          >
            {/* Below the breakpoint the same seven columns become a labelled card
                per line — every field present, nothing behind a sideways scroll. */}
            {isNarrow ? (
              <>
                <DataCardList ariaLabel="Line items review">
                  {lines.map((line, index) => (
                    <DataCard
                      key={line.id}
                      heading={`Line ${String(index + 1)}`}
                      fields={[
                        { label: 'Item', value: line.itemDescription || '—' },
                        { label: 'Category', value: line.categoryLabel || '—' },
                        { label: 'Quantity', value: line.quantity || '—' },
                        { label: 'Unit', value: line.unitOfMeasure || '—' },
                        { label: 'Delivery', value: line.deliveryDate || '—' },
                        {
                          label: 'Unit price',
                          value: line.netPrice ? formatNetPrice(line.netPrice, currencyCode) : '—',
                        },
                        { label: 'Start date', value: line.startDate || '—' },
                        { label: 'End date', value: line.endDate || '—' },
                        { label: 'Sales tax', value: line.salesTaxLabel || '—' },
                        {
                          label: 'Line total',
                          value: line.lineTotal ? formatTotal(line.lineTotal, currencyCode) : '—',
                        },
                      ]}
                    />
                  ))}
                </DataCardList>
                <DataCardTotal
                  label="Grand total"
                  value={`${formatTotal(grandTotal, currencyCode)} ${currencyCode}`}
                />
              </>
            ) : (
              <div className={surfaces.tableCard}>
                <div className={styles.scroll}>
                  <Table aria-label="Line items review" className={styles.table} size="medium">
                    <TableHeader>
                      <TableRow className={styles.headerRow}>
                        <TableHeaderCell
                          className={mergeClasses(styles.headerCell, styles.alignCenter)}
                        >
                          Item
                        </TableHeaderCell>
                        <TableHeaderCell
                          className={mergeClasses(styles.headerCell, styles.alignCenter)}
                        >
                          Category
                        </TableHeaderCell>
                        <TableHeaderCell
                          className={mergeClasses(
                            styles.headerCell,
                            styles.alignCenter,
                            styles.numericValue,
                          )}
                        >
                          Quantity
                        </TableHeaderCell>
                        <TableHeaderCell
                          className={mergeClasses(styles.headerCell, styles.alignCenter)}
                        >
                          Unit
                        </TableHeaderCell>
                        <TableHeaderCell
                          className={mergeClasses(styles.headerCell, styles.alignCenter)}
                        >
                          Delivery
                        </TableHeaderCell>
                        <TableHeaderCell
                          className={mergeClasses(
                            styles.headerCell,
                            styles.alignCenter,
                            styles.numericValue,
                          )}
                        >
                          Unit price
                        </TableHeaderCell>
                        <TableHeaderCell
                          className={mergeClasses(styles.headerCell, styles.alignCenter)}
                        >
                          Start date
                        </TableHeaderCell>
                        <TableHeaderCell
                          className={mergeClasses(styles.headerCell, styles.alignCenter)}
                        >
                          End date
                        </TableHeaderCell>
                        <TableHeaderCell
                          className={mergeClasses(styles.headerCell, styles.alignCenter)}
                        >
                          Sales tax
                        </TableHeaderCell>
                        <TableHeaderCell
                          className={mergeClasses(
                            styles.headerCell,
                            styles.alignCenter,
                            styles.numericValue,
                          )}
                        >
                          Line total
                        </TableHeaderCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lines.map((line) => (
                        <TableRow key={line.id}>
                          <TableCell className={mergeClasses(styles.cell, styles.alignCenter)}>
                            {line.itemDescription || '—'}
                          </TableCell>
                          <TableCell className={mergeClasses(styles.cell, styles.alignCenter)}>
                            {line.categoryLabel || '—'}
                          </TableCell>
                          <TableCell
                            className={mergeClasses(
                              styles.cell,
                              styles.alignCenter,
                              styles.numericValue,
                            )}
                          >
                            {line.quantity || '—'}
                          </TableCell>
                          <TableCell className={mergeClasses(styles.cell, styles.alignCenter)}>
                            {line.unitOfMeasure || '—'}
                          </TableCell>
                          <TableCell className={mergeClasses(styles.cell, styles.alignCenter)}>
                            {line.deliveryDate || '—'}
                          </TableCell>
                          <TableCell
                            className={mergeClasses(
                              styles.cell,
                              styles.alignCenter,
                              styles.numericValue,
                            )}
                          >
                            {line.netPrice ? formatNetPrice(line.netPrice, currencyCode) : '—'}
                          </TableCell>
                          <TableCell className={mergeClasses(styles.cell, styles.alignCenter)}>
                            {line.startDate || '—'}
                          </TableCell>
                          <TableCell className={mergeClasses(styles.cell, styles.alignCenter)}>
                            {line.endDate || '—'}
                          </TableCell>
                          <TableCell className={mergeClasses(styles.cell, styles.alignCenter)}>
                            {line.salesTaxLabel || '—'}
                          </TableCell>
                          <TableCell
                            className={mergeClasses(
                              styles.cell,
                              styles.alignCenter,
                              styles.numericValue,
                            )}
                          >
                            {line.lineTotal ? formatTotal(line.lineTotal, currencyCode) : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <tfoot>
                      <tr>
                        <td colSpan={9} className={mergeClasses(styles.cell, styles.footerCell)}>
                          <Body1Strong>Grand total</Body1Strong>
                        </td>
                        <td className={mergeClasses(styles.cell, styles.footerCell)}>
                          <Body1Strong>
                            {formatTotal(grandTotal, currencyCode)} {currencyCode}
                          </Body1Strong>
                        </td>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              </div>
            )}
          </Section>

          <Section
            level={3}
            title="Attachments"
            count={attachments.length}
            action={editAction(readOnly, onEditAttachments)}
          >
            {attachments.length === 0 ? (
              <Body1 as="p" className={styles.hint}>
                No files attached.
              </Body1>
            ) : (
              <div className={styles.attachmentsBlock}>
                {images.length > 0 && (
                  <div className={styles.thumbnailGrid}>
                    {images.map((attachment) => (
                      <ReviewSummaryThumbnail
                        key={attachment.id}
                        attachment={attachment}
                        localFile={localFiles?.get(attachment.id)}
                        onOpen={() => setPreviewIndex(attachments.indexOf(attachment))}
                      />
                    ))}
                  </div>
                )}
                {documents.map((attachment) => (
                  <div
                    key={attachment.id}
                    className={mergeClasses(surfaces.inset, styles.documentRow)}
                  >
                    <Body1 as="span" title={attachment.fileName}>
                      {truncateFileNameMiddle(attachment.fileName)}
                    </Body1>
                    <Button
                      appearance="secondary"
                      size="small"
                      onClick={() => setPreviewIndex(attachments.indexOf(attachment))}
                    >
                      Open
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      </Section>

      {previewIndex !== null && (
        <AttachmentPreviewDialog
          attachments={attachments}
          initialIndex={previewIndex}
          localFiles={localFiles}
          onClose={() => setPreviewIndex(null)}
        />
      )}
    </div>
  );
}
